// React, Next
import { useContext } from 'react'
import { useTranslation } from 'next-i18next'
import Image, { StaticImageData } from 'next/image'

// MUI
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Typography from '@mui/material/Typography'

// Icons
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos'

// Img
import IcRiskAssessment from 'public/img/icons/1_risk_assessment.png'
import IcAssetDiscovery from 'public/img/icons/2_asset_discovery.png'
import IcNetworkMapping from 'public/img/icons/3_network_mapping.png'
import IcPenTesting from 'public/img/icons/4_pen_testing.png'
import IcSecurityDesign from 'public/img/icons/5_security_design.png'
import IcSegmentation from 'public/img/icons/6_network_segmentation.png'
import IcFirewallReview from 'public/img/icons/7_firewall_review.png'
import IcHardening from 'public/img/icons/8_security_hardening.png'
import IcGapAssessment from 'public/img/icons/9_62443_gap_assessment.png'
import IcIncidentResponse from 'public/img/icons/10_incident_response.png'

// Custom
import Section from '@/components/sections/Section'
import { GlobalContext } from 'context/GlobalContextProvider'

// Style
import themeColors from 'assets/theme/colors'
import breakpoints from 'assets/theme/breakpoints'
import { getFontBody, getFontBodySmall, getFontH2 } from '@/assets/theme/fonts'

/** Official ISA page for the ISA/IEC 62443 series. */
const ISA_62443_URL =
  'https://www.isa.org/standards-and-publications/isa-standards/isa-iec-62443-series-of-standards'

export default function ServicesSection() {
  const { t } = useTranslation('servicesOtCybersecurity')
  const { screenDimensions } = useContext(GlobalContext)

  const w = screenDimensions.width
  const mobileMode = w < breakpoints.desktopToMobile

  // The icons have differing aspect ratios, so they are sized by height and
  // left to find their own width.
  const ICON_HEIGHT = 54

  const services: { icon: StaticImageData; title: string; text: string }[] = [
    {
      icon: IcRiskAssessment,
      title: t('services.items.01.title'),
      text: t('services.items.01.description')
    },
    {
      icon: IcAssetDiscovery,
      title: t('services.items.02.title'),
      text: t('services.items.02.description')
    },
    {
      icon: IcNetworkMapping,
      title: t('services.items.03.title'),
      text: t('services.items.03.description')
    },
    {
      icon: IcPenTesting,
      title: t('services.items.04.title'),
      text: t('services.items.04.description')
    },
    {
      icon: IcSecurityDesign,
      title: t('services.items.05.title'),
      text: t('services.items.05.description')
    },
    {
      icon: IcSegmentation,
      title: t('services.items.06.title'),
      text: t('services.items.06.description')
    },
    {
      icon: IcFirewallReview,
      title: t('services.items.07.title'),
      text: t('services.items.07.description')
    },
    {
      icon: IcHardening,
      title: t('services.items.08.title'),
      text: t('services.items.08.description')
    },
    {
      icon: IcGapAssessment,
      title: t('services.items.09.title'),
      text: t('services.items.09.description')
    },
    {
      icon: IcIncidentResponse,
      title: t('services.items.10.title'),
      text: t('services.items.10.description')
    }
  ]

  const getGrid = () => {
    if (w > breakpoints.desktopMid) return 'repeat(3, 1fr)'
    if (w > breakpoints.desktopToMobile) return 'repeat(3, 1fr)'
    if (w > breakpoints.mobileMid) return 'repeat(2, 1fr)'
    return 'repeat(1, 1fr)'
  }

  const getCard = (service: {
    icon: StaticImageData
    title: string
    text: string
  }) => (
    <Box
      key={service.title}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        background: themeColors.kroemtech.background,
        border: `1px solid ${themeColors.gray.lighter}`,
        borderRadius: '10px',
        padding: '1.75rem',
        boxSizing: 'border-box'
      }}
    >
      <Box
        sx={{
          height: `${ICON_HEIGHT}px`,
          marginBottom: '1.25rem',
          display: 'flex',
          alignItems: 'center'
        }}
      >
        <Image
          src={service.icon}
          alt=""
          style={{ height: `${ICON_HEIGHT}px`, width: 'auto' }}
        />
      </Box>

      <Typography
        variant="h3"
        component="h3"
        sx={{
          fontSize: '1.125rem',
          fontWeight: 600,
          color: themeColors.kroemtech.dunkelblau,
          marginBottom: '0.75rem'
        }}
      >
        {service.title}
      </Typography>

      <Typography
        sx={{
          fontSize: getFontBody(),
          lineHeight: 1.6,
          color: themeColors.gray.defaultText,
          flex: 1,
          marginBottom: '1.25rem'
        }}
      >
        {service.text}
      </Typography>

      {/* Links are not wired up yet - the arrow is decorative for now. */}
      <ArrowForwardIcon
        sx={{ fontSize: '1.25rem', color: themeColors.success.main }}
      />
    </Box>
  )

  const getAlignedBanner = () => (
    <Box
      sx={{
        display: 'flex',
        flexDirection: mobileMode ? 'column' : 'row',
        alignItems: mobileMode ? 'flex-start' : 'center',
        gap: mobileMode ? '1.25rem' : '2rem',
        background: themeColors.info.lightest,
        borderRadius: '10px',
        padding: mobileMode ? '1.75rem' : '2rem 2.5rem',
        marginTop: '2rem'
      }}
    >
      <Box
        sx={{
          flexShrink: 0,
          width: '4.5rem',
          height: '4.5rem',
          borderRadius: '50%',
          background: themeColors.kroemtech.background,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <Image
          src={IcGapAssessment}
          alt=""
          style={{ height: '44px', width: 'auto' }}
        />
      </Box>

      <Box sx={{ flex: 1 }}>
        <Typography
          variant="h3"
          component="h3"
          sx={{
            fontSize: '1.25rem',
            fontWeight: 600,
            color: themeColors.kroemtech.dunkelblau,
            marginBottom: '0.5rem'
          }}
        >
          {t('services.aligned.title')}
        </Typography>
        <Typography
          sx={{
            fontSize: getFontBody(),
            lineHeight: 1.6,
            color: themeColors.gray.defaultText
          }}
        >
          {t('services.aligned.description')}
        </Typography>
      </Box>

      <Button
        variant="contained"
        href={ISA_62443_URL}
        target="_blank"
        rel="noopener noreferrer"
        endIcon={<ArrowForwardIosIcon sx={{ transform: 'scale(0.7)' }} />}
        sx={{
          flexShrink: 0,
          background: themeColors.success.main,
          borderRadius: '4px',
          padding: '0.7rem 1.4rem',
          fontWeight: 600,
          letterSpacing: '0.03em',
          boxShadow: 'none',
          '&:hover': {
            background: themeColors.success.darker,
            boxShadow: 'none'
          }
        }}
      >
        {t('services.aligned.cta')}
      </Button>
    </Box>
  )

  const getContent = () => (
    <Box sx={{ width: '100%' }}>
      <Typography
        sx={{
          fontSize: getFontBodySmall(),
          fontWeight: 700,
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          color: themeColors.success.main,
          marginBottom: '0.75rem'
        }}
      >
        {t('services.eyebrow')}
      </Typography>

      <Typography
        variant="h2"
        component="h2"
        fontSize={getFontH2()}
        sx={{
          fontWeight: 700,
          color: themeColors.kroemtech.dunkelblau,
          marginBottom: '1.25rem'
        }}
      >
        {t('services.title')}
      </Typography>

      <Typography
        sx={{
          fontSize: getFontBody(),
          lineHeight: 1.7,
          color: themeColors.gray.defaultText,
          maxWidth: '48rem',
          marginBottom: '2.5rem'
        }}
      >
        {t('services.description')}
      </Typography>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: getGrid(),
          gap: '1.5rem',
          alignItems: 'stretch'
        }}
      >
        {services.map(service => getCard(service))}
      </Box>

      {getAlignedBanner()}
    </Box>
  )

  // *** MAIN RENDER ***
  return (
    <Section
      background={themeColors.section.light}
      paddingTop="5rem"
      paddingBottom="5rem"
      marginTop="6rem"
      content={getContent()}
    />
  )
}
